import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import chroma from "chroma-js";

const teamColors: { [id: string]: string[] } = {
  TBR: ["#02285C", "#90BDE7", "#FFFFFF"],
  BAL: ["#F25D23", "#000000", "#FFFFFF"],
  NYY: ["#132448", "#FFFFFF", "#FFFFFF"],
  BOS: ["#BD3039", "#0D2B56", "#FFFFFF"],
  TOR: ["#134A8E", "#E8291C", "#FFFFFF"],
  CLE: ["#E31937", "#002B5C", "#FFFFFF"],
  KCR: ["#004687", "#FFFFFF", "#FFFFFF"],
  DET: ["#0C2C56", "#FFFFFF", "#FFFFFF"],
  CHW: ["#000000", "#BFBFBF", "#FFFFFF"],
  MIN: ["#091F40", "#E31E34", "#FFFFFF"],
  HOU: ["#002D62", "#F4911E", "#FFFFFF"],
  SEA: ["#005C5C", "#0C2C56", "#FFFFFF"],
  TEX: ["#C0111F", "#003278", "#FFFFFF"],
  LAA: ["#BA0021", "#003263", "#C4CED4"],
  ATH: ["#00342C", "#FFB400", "#FFFFFF"],
  WSN: ["#AB0003", "#FFFFFF", "#FFFFFF"],
  NYM: ["#F75500", "#002C6E", "#FFFFFF"],
  MIA: ["#019CD6", "#E51829", "#FFFFFF"],
  PHI: ["#C21D39", "#01396F", "#FFFFFF"],
  ATL: ["#D3042B", "#001D3D", "#FFFFFF"],
  CIN: ["#C6011F", "#000000", "#FFFFFF"],
  PIT: ["#F7BF02", "#000000", "#FFFFFF"],
  CHC: ["#123581", "#E62F2E", "#FFFFFF"],
  MIL: ["#18294C", "#FEC430", "#FFFFFF"],
  STL: ["#BE0A15", "#FDDB00", "#FFFFFF"],
  LAD: ["#005A9D", "#FFFFFF", "#FFFFFF"],
  ARI: ["#C71128", "#00C2CE", "#FFFFFF"],
  SDP: ["#2D221C", "#FFC125", "#FFFFFF"],
  SFG: ["#231F20", "#F4793E", "#E8D8C4"],
  COL: ["#503E80", "#BFC2C9", "#FFFFFF"],
  FA: ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
  "0": ["#BFBFBF", "#FFFFFF", "#FFFFFF"],
};



export default function Heatmap(props: any) {
  const [pitchData, setPitchData] = useState<any[]>([]);
  const [hoveredZone, setHoveredZone] = useState<number | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const [stat, setStat] = React.useState("estimated_woba_using_speedangle");

  const handleStat = (event: SelectChangeEvent) => {
      setStat(event.target.value as string);
    };

  // Updated color scale with clear thresholds
  const getColor = (value: number) => {
    if (value >= 0.45) return "#D82129"; // Red for ≥ 0.45
    if (value <= 0.15) return "#3661AD"; // Blue for ≤ 0.15

    const scale = chroma.scale(["#3661AD", "#C2C2C2", "#D82129"]);
    return scale((value - .15) * 3.33).toString()
  };

  // Zone definitions with corrected boundaries
  const zones = [
    // Outer zones (no overlapping borders)
    { zone: 11, path: 'M0 0H150V150H0V0', x: 50, y: 25 },         // Top-left L
    { zone: 12, path: 'M150 0H300V150H150V0', x: 250, y: 25 },    // Top-right L
    { zone: 13, path: 'M0 150H150V300H0V150', x: 50, y: 285 },    // Bottom-left L
    { zone: 14, path: 'M150 150H300V300H150V150', x: 250, y: 285 }, // Bottom-right L

    // Inner 3x3 grid (strictly within 100-200 bounds)
    ...Array.from({ length: 9 }, (_, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      return {
        zone: i + 1,
        path: `M${50 + col * 66.66} ${50 + row * 66.66}h66.66v66.66h-66.66z`,
        x: 84 + col * 66.66,
        y: 84 + row * 66.66
      };
    })
  ];

  useEffect(() => {
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/hitter-heatmap/" + props.id + '/' + props.season + '/' + stat : "/hitter-heatmap/" + props.id + '/' + props.season + '/' + stat)
      .then((res) => res.json())
      .then((pData) => {
        //console.log("Pitch Data for ", props.season, pData);
        setPitchData(pData)
        //console.log("Pitches: ", p.toString())
      });
  }, [props.id, props.prod, props.season, stat]);

  const temp = Object.keys(props).includes('teamInfoProp')
  if (temp) {
    props.playerInfoProp.common = { teamInfo: props.teamInfoProp }
  }

  return (
    <div>
      <Grid
        container
        justifyContent="center"
        spacing={2}
      >
        <Grid maxWidth="90vw">
          <Box
            sx={{
              maxWidth: "90vw",
              width: "500px",
              border: "#293241 solid 2px",
              backgroundColor: "#FFFFFF",
              height: props.isDesktop ? "570px" : " 100%",
              boxShadow:
                "1px 1px 1px black,2px 2px 1px white,3px 3px 1px black,4px 4px 3px",
            }}
            justifyContent="flex-start"
            alignItems="center"
            display="flex"
            flexDirection="column"
          >
            <div
              style={{
                width: "100%",
                paddingTop: "10px",
                paddingBottom: "10px",
                backgroundColor:
                  props.playerInfoProp.common.teamInfo.MLB_AbbName !== null
                    ? teamColors[props.playerInfoProp.common.teamInfo.MLB_AbbName][0]
                    : teamColors["FA"][0],
              }}
            >
              {props.isDesktop &&
                (<Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography variant="h5">
                    <span
                      style={{
                        color:
                          props.playerInfoProp.common.teamInfo.MLB_AbbName !== null
                            ? teamColors[
                            props.playerInfoProp.common.teamInfo.MLB_AbbName
                            ][1]
                            : teamColors["FA"][1],
                        fontWeight: "bold",
                        fontSize: "25pt",
                        textAlign: "center"
                      }}
                    >
                      Heatmaps
                    </span>
                  </Typography>
                </Grid>)}
              {!props.isDesktop &&
                (<Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                ><Typography variant="h5" style={{ fontSize: "25pt" }}>
                    <div
                      style={{
                        color:
                          props.playerInfoProp.common.teamInfo.MLB_AbbName !== null
                            ? teamColors[
                            props.playerInfoProp.common.teamInfo.MLB_AbbName
                            ][1]
                            : teamColors["FA"][1],
                        fontWeight: "bold",
                      }}
                    >
                      Heatmaps
                    </div>
                  </Typography></Grid>)}
            </div>
            <div style={{marginTop: '20px'}}>
              <FormControl>
                <InputLabel id="demo-simple-select-label">Select Stat</InputLabel>
                <Select
                  value={stat}
                  label="Select Stat"
                  onChange={handleStat}
                  defaultValue="estimated_woba_using_speedangle"
                  sx={{ width: "102px" }}
                >
                  <MenuItem value={"estimated_woba_using_speedangle"}>xwOBA</MenuItem>
                  <MenuItem value={"estimated_ba_using_speedangle"}>xBA</MenuItem>
                  <MenuItem value={"woba_value"}>wOBA</MenuItem>
                  <MenuItem value={"babip_value"}>BABIP</MenuItem>
                  <MenuItem value={"iso_value"}>ISO</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div style={{ position: 'relative', width: '300px', height: '300px', marginTop: '15px' }}>
              <svg
                width="300"
                height="300"
                viewBox="0 0 300 300"
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
                }}
              >
                {/* Background */}
                <rect x="0" y="0" width="300" height="300" fill="#f0f0f0" />


                {/* Outer Zones */}
                {zones.slice(0, 4).map(({ zone, path, x, y }) => (
                  <g key={zone}>
                    <path
                      d={path}
                      fill={getColor(pitchData.find(d => d.zone === zone)?.value || 0)}
                      onMouseEnter={() => setHoveredZone(zone)}
                      onMouseLeave={() => setHoveredZone(null)}
                      style={{ cursor: 'pointer', transition: 'fill 0.2s' }}
                    />
                    <text x={x} y={y} fontSize="20" textAnchor="middle" fill="#000000">
                      {pitchData.find(d => d.zone === zone)?.value?.toFixed(3) || 0}
                    </text>
                  </g>
                ))}

                {/* Inner 3x3 Grid LAST */}
                {zones.slice(4).map(({ zone, path, x, y }) => (
                  <g key={zone}>
                    <path
                      d={path}
                      fill={getColor(pitchData.find(d => d.zone === zone)?.value || 0)}
                      onMouseEnter={() => setHoveredZone(zone)}
                      onMouseLeave={() => setHoveredZone(null)}
                      style={{ cursor: 'pointer', transition: 'fill 0.2s' }}
                    />
                    <text x={x} y={y} fontSize="15" textAnchor="middle" fill="#000000">
                      {pitchData.find(d => d.zone === zone)?.value?.toFixed(3) || 0}
                    </text>
                  </g>
                ))}

                {/* Inner grid lines ON TOP */}
                <line x1="117" y1="50" x2="117" y2="250" stroke="#000" strokeWidth="1" />
                <line x1="183" y1="50" x2="183" y2="250" stroke="#000" strokeWidth="1" />
                <line x1="50" y1="116" x2="250" y2="116" stroke="#000" strokeWidth="1" />
                <line x1="50" y1="183" x2="250" y2="183" stroke="#000" strokeWidth="1" />

                <line x1="50" y1="50" x2="250" y2="50" stroke="#000" strokeWidth="1" />
                <line x1="50" y1="250" x2="250" y2="250" stroke="#000" strokeWidth="1" />
                <line x1="50" y1="50" x2="50" y2="250" stroke="#000" strokeWidth="1" />
                <line x1="250" y1="50" x2="250" y2="250" stroke="#000" strokeWidth="1" />

                <line x1="0" y1="150" x2="50" y2="150" stroke="#000" strokeWidth="1" />
                <line x1="150" y1="0" x2="150" y2="50" stroke="#000" strokeWidth="1" />
                <line x1="150" y1="250" x2="150" y2="300" stroke="#000" strokeWidth="1" />
                <line x1="250" y1="150" x2="300" y2="150" stroke="#000" strokeWidth="1" />

                <line x1="0" y1="1" x2="300" y2="1" stroke="#000" strokeWidth="1" />
                <line x1="0" y1="299" x2="300" y2="299" stroke="#000" strokeWidth="1" />
                <line x1="1" y1="0" x2="1" y2="300" stroke="#000" strokeWidth="1" />
                <line x1="299" y1="0" x2="299" y2="300" stroke="#000" strokeWidth="1" />
              </svg>

              {/* Tooltip remains the same */}
              {hoveredZone && (
                <div style={{
                  position: 'absolute',
                  left: `${mousePos.x + 10}px`,
                  top: `${mousePos.y + 10}px`,
                  background: 'rgba(0,0,0,0.9)',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                  pointerEvents: 'none'
                }}>
                  <div>Zone {hoveredZone}</div>
                  <div>Value: {pitchData.find(d => d.zone === hoveredZone)?.value?.toFixed(3)}</div>
                </div>
              )}
            </div>
          </Box>
        </Grid>
      </Grid>
    </div>
  );
}